import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(null); // Lightbox state
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const fetchProjectData = async () => {
      setLoading(true);
      try {
        const res = await fetch(`https://api.dev.auxo.dk/public/by-slug/da/${slug}`);
        if (!res.ok) {
          if (res.status === 404) {
            navigate('/404');
          } else {
            navigate('/403');
          }
          return;
        }
        const data = await res.json();
        setProject(data);
      } catch (err) {
        console.error('Error fetching project detail:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProjectData();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-black" />
          <p className="text-black font-medium">Henter oplysninger...</p>
        </div>
      </div>
    );
  }

  if (!project) return null;

  const title = project.titel?.da || project.titel;
  const description = project.beskrivelse?.da || project.beskrivelse;
  const location = project.lokation?.da || project.lokation;
  const images = project.billeder || [];

  return (
    <>
      <Helmet>
        <title>{`${title} | Projekter & Referencer | Tømrerfirmaet RNS Djursland`}</title>
        <meta name="description" content={`Læs om projektet "${title}" udført i ${location || 'Djursland-området'}. Se billeder af håndværket og få inspiration til dit eget projekt.`} />
        <meta property="og:title" content={`${title} | Tømrerfirmaet RNS Djursland`} />
        <meta property="og:description" content={`Læs om projektet "${title}" udført i ${location || 'Djursland-området'}. Se billeder af håndværket.`} />
        {images[0] && <meta property="og:image" content={images[0]} />}
        <link rel="canonical" href={`https://rns.dk/referencer/${slug}`} />
        <html lang="da-DK" />
      </Helmet>

      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 space-y-12">
          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column: Images */}
            <div className="lg:col-span-7 space-y-6">
              {images[0] && (
                <button
                  onClick={() => setActiveImage(images[0])}
                  className="w-full h-[300px] sm:h-[450px] rounded-none overflow-hidden border border-black/10 cursor-pointer focus:outline-none focus:ring-2 focus:ring-black"
                >
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/${images[0].replace('https://assets.auxodev.dk/', '')}`}
                    alt={`${title} - Udført tømrerarbejde i ${location || 'Djursland'} af Tømrerfirmaet RNS`}
                    className="w-full h-full object-cover hover:scale-101 transition-transform duration-300"
                  />
                </button>
              )}

              {/* Additional Images Grid */}
              {images.length > 1 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {images.slice(1).map((imgUrl, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveImage(imgUrl)}
                      className="h-28 sm:h-36 rounded-none overflow-hidden border border-black/10 cursor-pointer focus:outline-none focus:ring-2 focus:ring-black relative group"
                    >
                      <img
                        src={`https://assets.auxodev.dk/cdn-cgi/image/width=400,height=300,fit=cover,quality=80,format=auto/${imgUrl.replace('https://assets.auxodev.dk/', '')}`}
                        alt={`${title} - Detaljebillede ${idx + 1} af håndværk og materialer`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <i className="fa-solid fa-magnifying-glass-plus text-black text-lg" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right Column: Details */}
            <div className="lg:col-span-5 space-y-6">
              <div className="space-y-4">
                {location && (
                  <div className="flex items-center gap-2 text-xs font-bold text-black uppercase tracking-wider">
                    <i className="fa-solid fa-location-dot" />
                    {location}
                  </div>
                )}
                <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-black leading-tight">
                  {title}
                </h1>
              </div>

              <div className="border-t border-black/10 pt-6 space-y-4">
                <h2 className="font-display font-bold text-xl text-black">
                  Om projektet
                </h2>
                <div className="relative">
                  <div
                    className={`text-black leading-relaxed space-y-4 text-sm sm:text-base transition-all duration-500 ease-in-out overflow-hidden ${
                      !isExpanded ? 'max-h-28' : 'max-h-[2000px]'
                    }`}
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                </div>
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="inline-flex items-center gap-2 text-black hover:underline font-bold text-sm cursor-pointer focus:outline-none"
                >
                  {isExpanded ? (
                    <>
                      Vis mindre
                      <i className="fa-solid fa-chevron-up text-xs" />
                    </>
                  ) : (
                    <>
                      Læs mere
                      <i className="fa-solid fa-chevron-down text-xs" />
                    </>
                  )}
                </button>
              </div>

              {project.tilknyttet_ydelse && (
                <div className="pt-6 border-t border-black/10">
                  <Link
                    to={`/ydelser/${project.tilknyttet_ydelse}`}
                    className="inline-flex items-center gap-2 text-black hover:underline font-bold text-sm"
                  >
                    Læs mere om den tilknyttede ydelse
                    <i className="fa-solid fa-arrow-right text-xs" />
                  </Link>
                </div>
              )}

              {/* Contact CTA */}
              <div className="bg-black text-white p-8 rounded-none space-y-6">
                <h3 className="font-display font-bold text-xl text-white">
                  Interesseret i et lignende projekt?
                </h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  Vi står altid klar til at tage en uforpligtende snak om dine ønsker og behov. Lad os hjælpe dig med at realisere dine byggedrømme.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <a
                    href="tel:22439147"
                    className="flex-1 bg-white hover:bg-white/90 text-black font-bold py-3 px-4 rounded-none text-center transition-all flex items-center justify-center gap-2 text-sm"
                  >
                    <i className="fa-solid fa-phone" />
                    Ring op
                  </a>
                  <Link
                    to="/kontakt"
                    className="flex-1 bg-transparent hover:bg-white/10 text-white border border-white font-bold py-3 px-4 rounded-none text-center transition-all flex items-center justify-center gap-2 text-sm"
                  >
                    <i className="fa-solid fa-envelope" />
                    Skriv besked
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setActiveImage(null)}
        >
          <button
            onClick={() => setActiveImage(null)}
            className="absolute top-6 right-6 text-white hover:text-white/80 text-3xl cursor-pointer focus:outline-none"
            aria-label="Luk galleri"
          >
            <i className="fa-solid fa-xmark" />
          </button>
          <div className="max-w-5xl max-h-[85vh] overflow-hidden rounded-none" onClick={(e) => e.stopPropagation()}>
            <img
              src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/${activeImage.replace('https://assets.auxodev.dk/', '')}`}
              alt={`${title} - Forstørret visning af projektet fra Tømrerfirmaet RNS`}
              className="w-full h-full object-contain max-h-[85vh] touch-pan-y"
            />
          </div>
        </div>
      )}
    </>
  );
}
